import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import { EMPTY_PLAN, PlanFields, planTermsToBody, type PlanTermsState } from './PlanFields.tsx';
import { describeModel, type AffiliateRow } from './types.ts';

/**
 * Set the commission model on one affiliate.
 *
 * THE RATE CARD IS NOT A TABLE OF MODELS. `/api/admin/affiliates/plans` groups
 * live plans by their shape, so two affiliates on identical terms collapse into
 * one row — there is no "model" entity to create on its own, and a plan exists
 * only attached to an affiliate. That is why this dialog asks WHICH affiliate
 * before it asks for terms, and why the drawer opens it with one already
 * chosen.
 *
 * Writing a plan closes the live row (`valid_to = now()`) and inserts the next.
 * The old row is not edited and `affiliate.conversion.plan_id` still points at
 * it, so every player already attributed keeps the terms they were acquired
 * under. The dialog says how many that is, and the response repeats the count
 * after the fact — "affects future cohorts only" is a promise worth counting
 * rather than asserting.
 */

const REASONS: readonly { value: string; label: string }[] = [
  { value: 'NEW_AGREEMENT', label: 'New agreement signed' },
  { value: 'RENEGOTIATED', label: 'Renegotiated with the partner' },
  { value: 'PERFORMANCE_UPLIFT', label: 'Performance uplift — better terms earned' },
  { value: 'PERFORMANCE_DOWNGRADE', label: 'Performance downgrade' },
  { value: 'MODEL_SWITCH', label: 'Switching model (revshare ↔ CPA ↔ hybrid)' },
  { value: 'CORRECTION', label: 'Correcting terms recorded wrongly' },
];

interface Result {
  ref: string;
  previousPlan: string;
  plan: string;
  playersLeftOnPreviousPlan: number;
  message: string;
}

export function CommissionModelDialog({
  open,
  onClose,
  onDone,
  affiliates,
  preselected,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  /** Everything in the current brand scope, for the picker. */
  affiliates: AffiliateRow[];
  /** Chosen already when the dialog is opened from an affiliate's drawer. */
  preselected: AffiliateRow | null;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [affiliateId, setAffiliateId] = useState(preselected?.id ?? '');
  const [plan, setPlan] = useState<PlanTermsState>(EMPTY_PLAN);
  const [reasonCode, setReasonCode] = useState(REASONS[0]!.value);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const chosen = affiliates.find((a) => a.id === (preselected?.id ?? affiliateId)) ?? preselected;

  const close = () => {
    setAffiliateId('');
    setPlan(EMPTY_PLAN);
    setReasonCode(REASONS[0]!.value);
    setNote('');
    setFields({});
    onClose();
  };

  const submit = async () => {
    const target = preselected?.id ?? affiliateId;
    const terms = planTermsToBody(plan);
    const problems: Record<string, string> = { ...terms.fields };
    if (target === '') problems['affiliateId'] = 'Pick the affiliate these terms apply to.';
    if (note.trim().length < 3) problems['note'] = 'Say why the terms are changing.';
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<Result>(`/api/admin/affiliates/${target}/plan`, {
        ...terms.body,
        reasonCode,
        note: note.trim(),
      });
      toast(result.message);
      close();
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not change the commission model',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      title={chosen === null || chosen === undefined ? 'Set a commission model' : `Commission model · ${chosen.ref}`}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Set model
          </Button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          A commission model belongs to an affiliate, not to the network — the rate card groups
          live plans by their shape, so two partners on identical terms show as one row there.
          Setting a model here closes the live plan and starts a new one from now.
        </p>

        {preselected === null ? (
          <Field label="Affiliate" error={fields['affiliateId']}>
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={affiliateId}
                onChange={(e) => setAffiliateId(e.target.value)}
              >
                <option value="">Select an affiliate</option>
                {affiliates.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.ref} · {a.name} — {describeModel(a.model, a.revshare_bp, a.cpa_amount)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {chosen && (
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {chosen.name} is on{' '}
              <strong>{describeModel(chosen.model, chosen.revshare_bp, chosen.cpa_amount)}</strong>.{' '}
              {/* The count IS the promise. An operator changing a rate is
                  entitled to know how many players it does not touch. */}
              <strong>{formatCount(Number(chosen.referred_players))}</strong> players already
              attributed keep those terms; the new model applies to players attributed from now.
            </div>
          </div>
        )}

        <PlanFields state={plan} onChange={setPlan} fields={fields} />

        <Field label="Why" error={fields['reasonCode']}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={fields['note']}
          hint="Recorded in the audit trail with the before and after terms."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setFields({});
              }}
              placeholder="Q3 review — moved from hybrid to flat revshare"
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
